/*
  Carter & Me — baby registry.

  =====================================================================
  HOW THIS PERSISTS, AND WHY

  The registry lives in two places:

    The owner's copy   -> localStorage, key cm:registry
    The shared copy    -> encoded into the share link itself

  There is no server and no app. "Create my registry" packs the name,
  date, note and chosen handles into base64url and hangs it off the URL
  as ?r=. A guest opening that link decodes it and renders the list. The
  link IS the registry.

  Customer metafields were the obvious route and they do not work: a
  Liquid theme can READ customer.metafields.* but has no way to WRITE
  them. Every documented write path needs an app — a customer account UI
  extension (Customer Account API, 2024-07+) or an Admin API token. The
  Storefront API's customerUpdate takes name, email, phone and password,
  and no metafields at all. A form posting to a metafield would have
  silently done nothing.

  What this route costs, stated plainly so nobody rediscovers it later:

    - The link is the only copy. Lose it and the registry is gone. The
      page pushes copying and emailing it to yourself for that reason.
    - Editing from a second device means opening your own link there.
    - Roughly 40 pieces before the URL gets unwieldy. Enforced below.

  RESERVE IS STILL NOT POSSIBLE, and that is unchanged from the original
  decision. A guest marking a gift reserved is a write that a DIFFERENT
  guest has to read, and a theme only ever runs in one visitor's browser.
  Marking it in local storage would be worse than not marking it, because
  the page would claim the problem is solved while the double-buy still
  happens.

  So guests can BUY from the list — that works, it is an ordinary add to
  cart — but nothing claims a piece is reserved for anyone else. The list
  carries an honest standing note instead. Wiring a registry app later
  replaces readList/encode/decode and nothing else.
  =====================================================================
*/

(function () {
  const KEY = 'cm:registry';
  const WISHLIST_KEY = 'cm:wishlist';
  const MAX_ITEMS = 40;

  /* ---------- storage ---------- */

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return null;
      if (!Array.isArray(parsed.handles)) parsed.handles = [];
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function write(data) {
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) {
      /* Private mode. The page still reflects this session. */
    }
  }

  function readWishlist() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* ---------- link encoding ---------- */

  // btoa is byte-oriented, so names with accents or emoji have to be
  // encoded to UTF-8 bytes first or it throws.
  function encode(data) {
    const json = JSON.stringify({
      n: data.name || '',
      d: data.date || '',
      m: data.note || '',
      p: (data.handles || []).slice(0, MAX_ITEMS),
    });
    const bytes = new TextEncoder().encode(json);
    let bin = '';
    bytes.forEach((b) => {
      bin += String.fromCharCode(b);
    });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decode(token) {
    try {
      const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
      const bin = atob(b64);
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      const obj = JSON.parse(new TextDecoder().decode(bytes));
      if (!obj || typeof obj !== 'object') return null;
      return {
        name: typeof obj.n === 'string' ? obj.n : '',
        date: typeof obj.d === 'string' ? obj.d : '',
        note: typeof obj.m === 'string' ? obj.m : '',
        handles: Array.isArray(obj.p) ? obj.p.filter((h) => typeof h === 'string') : [],
      };
    } catch (e) {
      // Truncated or hand-edited link. Treated as no registry rather
      // than a broken page.
      console.warn('[cm-registry] could not read the share link', e);
      return null;
    }
  }

  function shareUrl(data) {
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}?r=${encode(data)}`;
  }

  /* ---------- formatting ---------- */

  function formatDate(value, prefix) {
    if (!value) return '';
    // input[type=date] gives YYYY-MM-DD. Parse the parts rather than
    // letting Date() apply a timezone and slip a day.
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!parts) return value;
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    const month = months[Number(parts[2]) - 1] || '';
    return `${prefix ? prefix + ' ' : ''}${month} ${parts[1]}`.trim();
  }

  function titleFor(name, pattern) {
    if (!name) return '';
    return (pattern || "[name]'s registry").replace('[name]', name);
  }

  /* ---------- page ---------- */

  function boot() {
    // The three states are sibling sections, not nested in a wrapper, so
    // the document is the root and the presence of any state-tagged
    // section is what identifies the registry page.
    if (!document.querySelector('[data-cm-reg-state]')) return;
    const page = document;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('r');
    const shared = token ? decode(token) : null;

    // Arriving from the wishlist band. Both lists are in the same browser
    // under the same origin, so the link only has to say what to do; the
    // pieces are read straight out of cm:wishlist. Ignored on a share
    // link, where the visitor is looking at someone else's registry.
    const fromWishlist = !shared && params.get('from') === 'wishlist';
    if (fromWishlist) {
      const existing = read();
      if (existing) {
        // Merge into the registry they already have, skipping anything
        // already on it, so arriving twice is harmless.
        const merged = existing.handles.slice();
        readWishlist().forEach((h) => {
          if (!merged.includes(h) && merged.length < MAX_ITEMS) merged.push(h);
        });
        existing.handles = merged;
        write(existing);
      }
    }

    // Guest if the URL carries a readable registry, otherwise the
    // visitor's own, otherwise the setup form.
    const owned = read();
    const isGuest = !!shared;
    const data = shared || owned;
    const mode = isGuest ? 'guest' : owned ? 'live' : 'setup';

    document.documentElement.dataset.cmRegMode = mode;
    page.querySelectorAll('[data-cm-reg-state]').forEach((el) => {
      const states = el.dataset.cmRegState.split(' ');
      el.hidden = !states.includes(mode);
    });
    page.querySelectorAll('[data-cm-reg-owner-only]').forEach((el) => {
      el.hidden = isGuest;
    });

    fillHeader(page, data, isGuest);
    renderList(page, data, isGuest);
    wireForm(page, owned);
    wireImport(page);
    announceCarry(page, mode, fromWishlist);
  }

  // Tell someone arriving from the wishlist what is about to happen, or
  // what just did. Silent otherwise.
  function announceCarry(page, mode, fromWishlist) {
    // There is one of these in the setup panel and one in the live
    // header, because whichever state the visitor lands in, the other is
    // hidden.
    const nodes = page.querySelectorAll('[data-cm-reg-carried]');
    if (!nodes.length) return;
    const n = readWishlist().length;

    nodes.forEach((el) => {
      const template = mode === 'setup' ? el.dataset.setupText : el.dataset.liveText;
      if (!fromWishlist || n === 0 || !template) {
        el.hidden = true;
        return;
      }
      el.textContent = template
        .replace('[count]', String(n))
        .replace('[pieces]', n === 1 ? 'piece' : 'pieces');
      el.hidden = false;
    });
  }

  function fillHeader(page, data, isGuest) {
    if (!data) return;
    const host = page.querySelector('[data-cm-reg-header]');
    if (!host) return;

    const titleEl = host.querySelector('[data-cm-reg-title]');
    const dateEl = host.querySelector('[data-cm-reg-date]');
    const noteEl = host.querySelector('[data-cm-reg-note]');
    const urlEl = host.querySelector('[data-cm-reg-url]');
    const copyEl = host.querySelector('[data-cm-copy]');
    const statusEl = host.querySelector('[data-cm-reg-status]');

    const title = titleFor(data.name, host.dataset.titlePattern);
    if (titleEl) titleEl.textContent = title;
    if (dateEl) {
      dateEl.textContent = formatDate(data.date, host.dataset.datePrefix);
      dateEl.hidden = !dateEl.textContent;
    }
    if (noteEl) {
      noteEl.textContent = data.note || '';
      noteEl.hidden = !data.note;
    }
    if (statusEl && isGuest) {
      statusEl.textContent = statusEl.dataset.guestLabel || statusEl.textContent;
    }

    const url = shareUrl(data);
    if (urlEl) urlEl.value = url;
    if (copyEl) copyEl.dataset.copyValue = url;

    host.querySelectorAll('[data-cm-reg-share]').forEach((link) => {
      const kind = link.dataset.cmRegShare;
      const text = `${title}: ${url}`;
      if (kind === 'whatsapp') {
        link.href = `https://wa.me/?text=${encodeURIComponent(text)}`;
      } else if (kind === 'email') {
        link.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
      }
    });
  }

  /* ---------- the gift list ---------- */

  function renderList(page, data, isGuest) {
    const host = page.querySelector('[data-cm-reg-list]');
    if (!host) return;

    const grid = host.querySelector('[data-cm-reg-grid]');
    const empty = host.querySelector('[data-cm-reg-empty]');
    const countEl = host.querySelector('[data-cm-reg-count]');
    const handles = (data && data.handles) || [];

    const one = countEl ? countEl.dataset.one || 'piece' : 'piece';
    const many = countEl ? countEl.dataset.many || 'pieces' : 'pieces';

    // boot() re-runs after create, edit and import, and each run builds a
    // fresh card map. Without clearing the grid the previous run's nodes
    // survive and every card is duplicated in the DOM while the count and
    // the stored list still read correctly, which makes it look like a
    // storage bug rather than a rendering one.
    grid.replaceChildren();

    const cards = new Map();
    const inFlight = new Set();

    function paint() {
      // Re-append in saved order: cards arrive in fetch-completion order.
      currentHandles().forEach((h) => {
        const el = cards.get(h);
        if (el) grid.appendChild(el);
      });
      const n = cards.size;
      if (countEl) countEl.textContent = n ? `${n} ${n === 1 ? one : many}` : '';
      const settled = inFlight.size === 0;
      if (empty) empty.hidden = !(settled && n === 0);
      grid.hidden = n === 0;
    }

    function currentHandles() {
      if (isGuest) return handles;
      const owned = read();
      return owned ? owned.handles : [];
    }

    function removeHandle(handle) {
      const owned = read();
      if (!owned) return;
      owned.handles = owned.handles.filter((h) => h !== handle);
      write(owned);
      const el = cards.get(handle);
      if (el) el.remove();
      cards.delete(handle);
      fillHeader(page, owned, false);
      paint();
    }

    async function add(handle) {
      if (cards.has(handle) || inFlight.has(handle)) return;
      inFlight.add(handle);
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}?view=registry-card`);
        if (!res.ok) throw new Error(String(res.status));
        const html = (await res.text()).trim();
        if (!html) throw new Error('empty');
        if (!currentHandles().includes(handle)) return;

        const wrap = document.createElement('div');
        wrap.className = 'cm-reg__item';
        wrap.dataset.handle = handle;
        wrap.innerHTML = html;

        // Owner gets a remove control. A guest must never be able to
        // edit someone else's list.
        if (!isGuest) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'cm-reg__remove';
          remove.textContent = host.dataset.removeLabel || 'Remove';
          remove.addEventListener('click', () => removeHandle(handle));
          wrap.appendChild(remove);
        }

        grid.appendChild(wrap);
        cards.set(handle, wrap);
      } catch (e) {
        // Unpublished, deleted or renamed. Drop it from the owner's copy
        // so it stops costing a request; a guest's link is read-only, so
        // the piece is just skipped.
        console.warn('[cm-registry] dropping unresolvable handle', handle, e);
        if (!isGuest) {
          const owned = read();
          if (owned) {
            owned.handles = owned.handles.filter((h) => h !== handle);
            write(owned);
          }
        }
      } finally {
        inFlight.delete(handle);
        paint();
      }
    }

    host.cmAdd = add;
    host.cmPaint = paint;
    paint();
    currentHandles().forEach(add);
  }

  /* ---------- setup form ---------- */

  function wireForm(page, owned) {
    const form = page.querySelector('[data-cm-registry-create]');
    if (!form) return;

    if (owned) {
      const name = form.querySelector('[name="registry[name]"]');
      const date = form.querySelector('[name="registry[date]"]');
      const note = form.querySelector('[name="registry[note]"]');
      if (name) name.value = owned.name || '';
      if (date) date.value = owned.date || '';
      if (note) note.value = owned.note || '';
    }

    form.addEventListener('submit', (event) => {
      // Nothing to post to. The registry is the link.
      event.preventDefault();
      const fd = new FormData(form);
      const existing = read();
      const data = {
        name: (fd.get('registry[name]') || '').toString().trim(),
        date: (fd.get('registry[date]') || '').toString(),
        note: (fd.get('registry[note]') || '').toString().trim(),
        // Seed from the wishlist so a new registry is not empty, unless
        // one is already being edited.
        handles: existing ? existing.handles : readWishlist().slice(0, MAX_ITEMS),
      };
      if (!data.name) return;
      write(data);
      // Re-run rather than patching state by hand, so every panel,
      // the share link and the list all agree.
      boot();
      const header = page.querySelector('[data-cm-reg-header]');
      if (header) header.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    page.querySelectorAll('[data-cm-reg-edit]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        page.querySelectorAll('[data-cm-reg-state]').forEach((el) => {
          el.hidden = !el.dataset.cmRegState.split(' ').includes('setup');
        });
        form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ---------- import from the wishlist ---------- */

  function wireImport(page) {
    page.querySelectorAll('[data-cm-reg-import]').forEach((button) => {
      const saved = readWishlist();
      const countEl = button.querySelector('[data-cm-reg-import-count]');
      if (countEl) countEl.textContent = String(saved.length);
      button.hidden = saved.length === 0;

      button.addEventListener('click', () => {
        const owned = read();
        if (!owned) return;
        const merged = owned.handles.slice();
        saved.forEach((h) => {
          if (!merged.includes(h) && merged.length < MAX_ITEMS) merged.push(h);
        });
        owned.handles = merged;
        write(owned);
        boot();
      });
    });
  }

  /* ---------- copy the share link ---------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cm-copy]');
    if (!button) return;

    const value = button.dataset.copyValue;
    const idle = button.dataset.labelIdle || 'Copy link';
    const done = button.dataset.labelDone || 'Copied';

    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      // Clipboard is blocked without a secure context or permission.
      // Select the field so the visitor can copy by hand.
      const field = document.querySelector('[data-cm-reg-url]');
      if (field) {
        field.focus();
        field.select();
      }
      return;
    }

    button.textContent = done;
    button.disabled = true;
    setTimeout(() => {
      button.textContent = idle;
      button.disabled = false;
    }, 1800);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
